/**
 * The bottom tab bar — the mobile shell's only permanent chrome.
 *
 * **Which tabs exist is the brand's answer, not this file's.** The keys are
 * whatever `brand.inSlot("mobile", "tab")` returns, wearing whatever that brand
 * calls them, plus a More key that is always last — see
 * `lib/brand-registry.ts`. With no profile that is List, Chart and Trade in
 * that order, which is what it has always been.
 *
 * Two things follow from the bar being computed rather than written out:
 *
 * - **The grid is counted, never assumed.** It was `repeat(4, 1fr)` while there
 *   were four keys. A fixed fraction and a variable number of children is the
 *   sizing bug in `docs/TODO.md`'s standing form: nothing errors, the keys just
 *   stop meeting the edge of the screen.
 * - **The bar holds five features and no more**, because `SLOT_CAPACITY` says
 *   so and `resolveBrand` moves the overflow elsewhere before it gets here.
 *   This draws what it is given; it does not police the count.
 *
 * One tab behaves differently: **tapping Chart while already on Chart advances
 * to the next chart group**. That is not an easter egg, it is
 * the chart pager's whole gesture — see [mobile.md](../../../../docs/features/mobile.md)
 * for the three swipe schemes it replaced and why each of them lost to it.
 *
 * The badge over the Chart icon reads `2/3`: **which chart, of how many**.
 * Position alone never says how many taps until it comes round; the total alone
 * never says where you are, and two charts on one symbol make the toolbar no
 * help either. Below two chart groups there is nothing to cycle and nothing to
 * count, so it is not drawn at all.
 *
 * Icons are inline SVG, as in `GridToolbarButton` and for its reason: four of
 * them do not justify a dependency.
 */

import type { ReactNode } from "react";

import { BRAND } from "../lib/brand-current";
import type { FeatureId } from "../lib/brand-registry";

/**
 * A tab is a feature, or the More key.
 *
 * More is not a feature and cannot be turned off, moved or renamed: it is the
 * door to everything that did not fit, so a bar without it is a bar with no way
 * on to the rest of the app.
 */
export type MobileTab = FeatureId | "more";

export interface MobileTabBarProps {
  active: MobileTab;
  /** A tab was chosen. Chart while on Chart arrives here too — see `onCycle`. */
  onSelect: (tab: MobileTab) => void;
  /** Chart was tapped while Chart was showing. Omitted ⇒ the tap does nothing. */
  onCycle?: () => void;
  /** How many chart groups the layout has, for the badge. */
  chartCount: number;
  /** Which one is showing, zero-based. */
  chartIndex: number;
  /**
   * A rail down the left edge instead of a bar along the bottom: a phone in
   * landscape (owner, 2026-10-13; native-apps.md §9.2). Same keys, same order.
   */
  rail?: boolean;
}

/**
 * 56px keys with the home indicator's inset below them.
 *
 * `env(safe-area-inset-bottom)` is why the shell asks for `viewport-fit=cover`
 * in `index.html`: without it the value is always zero and the last row of keys
 * sits under the gesture bar on every modern iPhone.
 */
const BAR: React.CSSProperties = {
  flex: "0 0 auto",
  display: "grid",
  // Set from the key count at render — see the header.
  background: "var(--qf-surface)",
  borderTop: "1px solid var(--qf-border)",
  paddingBottom: "env(safe-area-inset-bottom)",
};

/**
 * The landscape form: 56px wide, keys sharing the height.
 *
 * It takes the left inset (the notch, when the phone is turned that way) and the
 * bottom one, since it now runs past the home indicator.
 */
const RAIL: React.CSSProperties = {
  flex: "0 0 auto",
  // Last in the shell's markup, first on screen: the views keep their order and
  // their mounted state whichever way the phone is held.
  order: -1,
  width: 56,
  display: "grid",
  background: "var(--qf-surface)",
  borderRight: "1px solid var(--qf-border)",
  paddingLeft: "env(safe-area-inset-left)",
  paddingBottom: "env(safe-area-inset-bottom)",
  boxSizing: "content-box",
};

const KEY: React.CSSProperties = {
  height: 56,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  border: 0,
  background: "none",
  font: "inherit",
  fontSize: 10,
  letterSpacing: "0.02em",
  cursor: "pointer",
  // A grey flash over a tab that is about to change the whole screen reads as a
  // rendering fault; the colour change already says the tap landed.
  WebkitTapHighlightColor: "transparent",
};

const BADGE: React.CSSProperties = {
  position: "absolute",
  top: -5,
  right: -15,
  minWidth: 16,
  height: 16,
  padding: "0 5px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  lineHeight: "16px",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
};

/**
 * The glyph for a feature that can be a tab.
 *
 * Icons are inline SVG and therefore React, which is why this mapping is here
 * rather than in the registry — `lib/` holds no components (see this app's
 * CLAUDE.md). A feature given a `tab` slot with no icon here draws the fallback
 * rather than an empty key, so adding one to the registry degrades to something
 * tappable instead of to a gap.
 */
/** Two speech bubbles — a conversation, not a single message. */
function ChatIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 3.2h8.2v5.4H5.4L3 10.6V8.6H2Z" />
      <path d="M12.2 5.6H14v5.4h-1v2l-2.4-2H6.8V10" />
    </svg>
  );
}

function DataIcon() {
  return (
    <svg {...ICON}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 10h16M10 10v10" />
    </svg>
  );
}

const TAB_ICONS: Partial<Record<FeatureId, ReactNode>> = {
  data: <DataIcon />,
  community: <ChatIcon />,
  screener: <ListIcon />,
  charts: <ChartIcon />,
  journal: <TradeIcon />,
};

export function MobileTabBar({
  active,
  onSelect,
  onCycle,
  chartCount,
  chartIndex,
  rail = false,
}: MobileTabBarProps) {
  const tab = (id: MobileTab, label: string, icon: ReactNode, badge?: ReactNode) => {
    const on = active === id;
    return (
      <button
        key={id}
        onClick={() => {
          // The one tab whose second tap means something. Everything else
          // treats a tap on itself as the no-op it looks like.
          if (id === "charts" && on && onCycle) onCycle();
          else onSelect(id);
        }}
        aria-selected={on}
        aria-label={label}
        // In the rail the keys share the height rather than each taking 56px:
        // six of them fill a sideways phone's ~372px exactly.
        style={{
          ...KEY,
          ...(rail ? { height: "auto", minHeight: 44 } : null),
          color: on ? "var(--qf-accent)" : "var(--qf-text-muted)",
        }}
      >
        <span style={{ position: "relative", display: "flex" }}>
          {icon}
          {badge}
        </span>
        {label}
      </button>
    );
  };

  const badge =
    chartCount > 1 ? (
      <span
        style={{
          ...BADGE,
          // Lit on the active tab and quiet elsewhere, like the label under it —
          // an accent badge over a grey icon reads as an alert.
          background: active === "charts" ? "var(--qf-accent)" : "var(--qf-text-muted)",
          color: active === "charts" ? "var(--qf-background)" : "var(--qf-surface)",
        }}
      >
        {chartIndex + 1}/{chartCount}
      </span>
    ) : undefined;

  const keys = BRAND.inSlot("mobile", "tab");

  return (
    <nav
      style={
        rail
          ? { ...RAIL, gridTemplateRows: `repeat(${keys.length + 1}, 1fr)` }
          : { ...BAR, gridTemplateColumns: `repeat(${keys.length + 1}, 1fr)` }
      }
    >
      {keys.map((feature) =>
        tab(
          feature.id,
          feature.label,
          TAB_ICONS[feature.id] ?? <DotIcon />,
          feature.id === "charts" ? badge : undefined,
        ),
      )}
      {tab("more", "More", <MoreIcon />)}
    </nav>
  );
}

const ICON = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

function ListIcon() {
  return (
    <svg {...ICON}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg {...ICON}>
      <path d="M4 19V5M4 19h16" />
      <path d="M8 15v-4M12 17V8M16 13V6" />
    </svg>
  );
}

/** Two arrows — a position going on and coming off, not a currency glyph. */
function TradeIcon() {
  return (
    <svg {...ICON}>
      <path d="M7 17V9m0 0L4 12m3-3l3 3" />
      <path d="M17 7v8m0 0l3-3m-3 3l-3-3" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg {...ICON}>
      <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** The fallback key glyph: a feature placed on the bar that has no icon yet. */
function DotIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="7" />
    </svg>
  );
}
